import { useCallback, useRef } from 'react';

/**
 * Hook for playing scan feedback sounds.
 * Uses Web Audio API for instant playback without latency.
 */
export function useScanSound() {
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playBeep = useCallback((frequency: number = 800, duration: number = 150, type: OscillatorType = 'square') => {
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration / 1000);
    } catch (e) {
      // Fallback: do nothing if audio fails
    }
  }, [getAudioContext]);

  const playSuccess = useCallback(() => {
    // Pleasant short beep
    playBeep(1000, 100, 'sine');
  }, [playBeep]);

  const playError = useCallback(() => {
    // Two short low beeps
    playBeep(300, 100, 'square');
    setTimeout(() => playBeep(200, 150, 'square'), 150);
  }, [playBeep]);

  const playAddItem = useCallback(() => {
    // Quick high beep for item added
    playBeep(1200, 80, 'sine');
  }, [playBeep]);

  const speak = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.3;
      utterance.volume = 0.7;
      speechSynthesis.speak(utterance);
    }
  }, []);

  return { playSuccess, playError, playAddItem, speak };
}
